import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Users, ArrowRight, Eye, LayoutGrid, UserCircle2 } from "lucide-react";
import { ClassScoreboard } from "@/components/ClassScoreboard";
import { BadgeShelf } from "@/components/BadgeShelf";
import { PointsLedger } from "@/components/PointsLedger";
import { CompositeMetricCards } from "@/components/CompositeMetricCards";
import { ProfileCard } from "@/components/ProfileCard";

export default function TeacherDashboard() {
  const nav = useNavigate();
  const [students, setStudents] = useState([]);
  const [school, setSchool] = useState(null);

  useEffect(() => {
    http.get("/students").then((r) => setStudents(r.data)).catch(() => toast.error("Failed to load class list"));
    http.get("/schools").then((r) => {
      const first = r.data?.[0];
      if (first) http.get(`/schools/${first.id}`).then((d) => setSchool(d.data));
    }).catch(() => {});
  }, []);

  return (
    <AppShell
      subtitle="Teacher · view only"
      title="Your class at a glance"
      right={<Badge variant="outline" className="text-xs"><Users className="w-3 h-3 mr-1" /> {students.length} students</Badge>}
    >
      <Tabs defaultValue="scoreboard" className="w-full">
        <TabsList className="bg-white border border-slate-200 p-1 rounded-xl h-auto" data-testid="teacher-tabs">
          <TabsTrigger value="scoreboard" data-testid="tab-scoreboard"><LayoutGrid className="w-4 h-4 mr-1" /> Class Scoreboard</TabsTrigger>
          <TabsTrigger value="roster" data-testid="tab-roster"><Users className="w-4 h-4 mr-1" /> Students</TabsTrigger>
          <TabsTrigger value="account" data-testid="tab-account"><UserCircle2 className="w-4 h-4 mr-1" /> Account</TabsTrigger>
        </TabsList>

        <TabsContent value="scoreboard" className="mt-6 space-y-8">
          <ClassScoreboard />

          {school?.composite_metrics && (
            <div>
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-sky-600 mb-3">School Composite Metrics</div>
              <CompositeMetricCards composite={school.composite_metrics} />
            </div>
          )}

          {school?.school && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-base">School journey · 8 KOI levels</CardTitle>
                  <Badge className="uppercase tracking-widest text-[10px]" style={{ background: school.gamification?.current?.color, color: "white" }}>
                    Lv {school.gamification?.current?.level} · {school.gamification?.current?.name}
                  </Badge>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {(school.school.points || 0).toLocaleString()} points earned
                  {school.gamification?.next && ` · ${school.gamification.points_to_next.toLocaleString()} to ${school.gamification.next.name}`}
                </div>
              </CardHeader>
              <CardContent>
                <BadgeShelf points={school.school.points || 0} testId="teacher-journey-shelf" />
              </CardContent>
            </Card>
          )}

          {school?.school && (
            <PointsLedger entityType="school" entityId={school.school.id} title="What earned these school points" />
          )}
        </TabsContent>

        <TabsContent value="roster" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg">My students</CardTitle>
              <div className="text-sm text-slate-500">Screening data is entered by the School Admin.</div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="teacher-students-table">
                  <thead className="text-slate-400 uppercase text-[10px] tracking-widest">
                    <tr>
                      <th className="text-left py-2 px-2">KID ID</th>
                      <th className="text-left py-2 px-2">Name</th>
                      <th className="text-left py-2 px-2">Class</th>
                      <th className="text-left py-2 px-2">Level</th>
                      <th className="text-right py-2 px-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((s) => (
                      <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                        <td className="py-2.5 px-2 font-mono text-slate-500">{s.kid_id}</td>
                        <td className="py-2.5 px-2 font-semibold text-slate-800">{s.name}</td>
                        <td className="py-2.5 px-2 text-slate-600">{s.grade}{s.section}</td>
                        <td className="py-2.5 px-2">
                          <Badge style={{ background: s.gamification?.current?.color, color: "white" }}>
                            Lv {s.gamification?.current?.level} · {s.gamification?.current?.name}
                          </Badge>
                        </td>
                        <td className="py-2.5 px-2 text-right">
                          <Button
                            size="sm"
                            data-testid={`view-student-${s.id}`}
                            onClick={() => nav(`/teacher/student/${s.id}`)}
                          >
                            <Eye className="w-4 h-4 mr-1" /> View report <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="account" className="mt-6">
          <ProfileCard
            title="Teacher account"
            subtitle="Update your contact details. The next OTP will be sent to the new email."
          />
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}
